import { useUserInfo } from "@/api/user";
import { useAdminPanelNavigationItems } from "@/components/AdminPanel/AdminPanel";
import { ROUTES } from "@/constants/routes";
import type { OpenSettingsInput } from "@/utils/settingsNavigation";
import { isPermittedSettingsDeepLink } from "@/utils/settingsNavigation";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Opens the settings dialog when the URL is under `/settings/*`.
 * Navigates home when the user closes the dialog while still on a settings URL.
 */
export function useSettingsRouteSync(
  openSettings: (input?: OpenSettingsInput) => void,
  settingsDialogOpen: boolean,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: isUserInfoLoading } = useUserInfo();
  const { allMenuItems } = useAdminPanelNavigationItems();
  const lastSyncedPathRef = useRef<string | null>(null);
  const wasDialogOpenRef = useRef(false);

  // Open the dialog when the browser lands on (or navigates to) a settings URL.
  useEffect(() => {
    if (!location.pathname.startsWith(ROUTES.SETTINGS.ROOT)) {
      lastSyncedPathRef.current = null;
      return;
    }

    if (isUserInfoLoading) {
      return;
    }

    if (lastSyncedPathRef.current === location.pathname) {
      return;
    }

    lastSyncedPathRef.current = location.pathname;

    if (!isPermittedSettingsDeepLink(location.pathname, allMenuItems)) {
      return;
    }

    openSettings(location.pathname);
  }, [location.pathname, openSettings, isUserInfoLoading, allMenuItems]);

  // Leave settings URLs only after the user closes an open dialog — not on mount
  // while the open effect above is still pending.
  useEffect(() => {
    if (
      wasDialogOpenRef.current &&
      !settingsDialogOpen &&
      location.pathname.startsWith(ROUTES.SETTINGS.ROOT)
    ) {
      navigate(ROUTES.HOME, { replace: true });
    }
    wasDialogOpenRef.current = settingsDialogOpen;
  }, [settingsDialogOpen, location.pathname, navigate]);
}
